// Peak Priorities Base Camp Glossary
// Canonical definitions of key terms and concepts
// Comprehensive glossary with all terms from TERMS_AND_DEFINITIONS.md

export interface AlternateDefinition {
  definition: string;
  context?: string; // When this definition applies
  examples?: string[];
  source?: string; // Source of this specific alternate definition (e.g., 'Managing Priorities')
}

export interface GlossaryEntry {
  term: string;
  definition: string;
  relatedFramework?: string; // encodedId of related framework/model
  category: 'framework' | 'concept' | 'technique' | 'principle' | 'anti-pattern';
  examples?: string[];
  seeAlso?: string[]; // Other related terms
  alternateDefinitions?: AlternateDefinition[]; // Additional valid definitions (polysemy support)
  source?: string; // Source of definition (e.g., 'Managing Priorities', 'Peak Priorities System')
  qualityLevel?: 1 | 2 | 3 | 4; // Quality level for book definitions: 1=explicit, 2=mostly explicit, 3=inference, 4=alternate
}

export interface ConceptualGroup {
  name: string;
  description: string;
  relatedFramework?: string;
  terms: string[]; // Array of term names from glossaryEntries
}

export const glossaryEntries: GlossaryEntry[] = [
  // ============================================
  // DEGAP Process Model
  // ============================================
  {
    term: 'DEGAP Process Model',
    definition: 'The essential 5-phase process for successful prioritization when it matters most. DEGAP is both a structural process and a functional discipline that applies throughout strategic, operational, and tactical work.',
    relatedFramework: 'pf.degap.stages',
    category: 'framework',
    examples: [
      'Using DEGAP to structure a strategic transformation from legacy systems to modern platform',
      'Applying DEGAP stages to organizational change initiatives'
    ],
    seeAlso: ['Decide', 'Engage', 'Gather', 'Arrange', 'Prioritize']
  },
  {
    term: 'Decide',
    definition: 'The first phase of DEGAP. Decide to prioritize prioritization (or not). This is the moment of recognition that prioritization is needed and the commitment to engage in the process.',
    relatedFramework: 'pf.degap.stages',
    category: 'concept',
    examples: [
      'Leadership decides to transform the product development process and commits resources',
      'Executive team decides to address organizational capability gaps in strategic thinking'
    ],
    seeAlso: ['DEGAP Process Model', 'Engage', 'Gather'],
    alternateDefinitions: [
      {
        definition: 'The DEGAP phase dedicated to whether the upside benefits of carving out the time and going through the process of prioritizing intentionally will outweigh the downside costs of simply letting priorities emerge as reality reveals itself.',
        context: 'Managing Priorities book (Level 2)',
        source: 'Managing Priorities'
      }
    ]
  },
  {
    term: 'Engage',
    definition: 'The second phase of DEGAP. Engage in the process with commitment. This involves getting stakeholders aligned, setting expectations, and ensuring everyone is ready to participate meaningfully.',
    relatedFramework: 'pf.degap.stages',
    category: 'concept',
    examples: [
      'Conducting stakeholder workshops to engage teams in the transformation vision',
      'Creating engagement forums where people can voice concerns and contribute ideas'
    ],
    seeAlso: ['DEGAP Process Model', 'Decide', 'Gather', 'Stakeholder Intelligence'],
    alternateDefinitions: [
      {
        definition: 'The DEGAP phase focused on carving out the necessary time and space to follow the workflow, identifying stakeholders who influence or are influenced by the process, and committing to action.',
        context: 'Managing Priorities book (Level 2)',
        source: 'Managing Priorities'
      }
    ]
  },
  {
    term: 'Gather',
    definition: 'The third phase of DEGAP. Gather the items to be prioritized. This involves collecting all potential items, ideas, requests, and opportunities that need to be considered.',
    relatedFramework: 'pf.degap.stages',
    category: 'concept',
    examples: [
      'Gathering capability assessments, process maps, and stakeholder input',
      'Collecting data on current performance metrics and future requirements'
    ],
    seeAlso: ['DEGAP Process Model', 'Engage', 'Arrange'],
    alternateDefinitions: [
      {
        definition: 'The DEGAP phase involving detective work to investigate what matters and why, including soliciting input from stakeholders, and uncovering potential priorities and pivotal insights.',
        context: 'Managing Priorities book (Level 2)',
        source: 'Managing Priorities'
      }
    ]
  },
  {
    term: 'Arrange',
    definition: 'The fourth phase of DEGAP. Arrange the items and information you\'ve gathered. This involves organizing and structuring the gathered items so they can be effectively compared and evaluated.',
    relatedFramework: 'pf.degap.stages',
    category: 'concept',
    examples: [
      'Arranging stakeholder inputs into themes and patterns',
      'Structuring gathered data into framework categories for analysis'
    ],
    seeAlso: ['DEGAP Process Model', 'Gather', 'Prioritize'],
    alternateDefinitions: [
      {
        definition: 'The DEGAP phase dedicated to reviewing, cleaning up, normalizing, and quantifying the relative value of potential priorities and associated information collected during the Gather phase.',
        context: 'Managing Priorities book (Level 2)',
        source: 'Managing Priorities'
      }
    ]
  },
  {
    term: 'Prioritize',
    definition: 'The fifth phase of DEGAP. Prioritize using frameworks, sorting techniques, etc. This is where items are evaluated, compared, ranked, and committed to specific positions in Item Pools.',
    relatedFramework: 'pf.degap.stages',
    category: 'concept',
    examples: [
      'Prioritizing capability development initiatives based on strategic impact and feasibility',
      'Sequencing transformation activities to maximize value and minimize risk'
    ],
    seeAlso: ['DEGAP Process Model', 'Arrange', 'Priority', 'Tradeoff Intelligence'],
    alternateDefinitions: [
      {
        definition: 'The final DEGAP phase where items are arranged in order of importance through the use of frameworks, sorting techniques, simulations, or hybrid approaches, empowering people to create better plans and make smarter decisions.',
        context: 'Managing Priorities book (Level 2)',
        source: 'Managing Priorities'
      }
    ]
  },

  // ============================================
  // Lifecycle States
  // ============================================
  {
    term: 'Item',
    definition: 'The primary object of the prioritization system. Represents a potential commitment that may originate as an idea, request, issue, opportunity, or signal. Items have lifecycle_stage (e.g. intake, structuring, ranking) and may have attributes and values. When an item is placed in a list (item pool) with a rank, it is a priority in that context; priority is contextual (list membership + rank), not a lifecycle state.',
    source: 'Managing Priorities',
    qualityLevel: 2,
    category: 'concept',
    examples: [
      'A new feature request enters as an item',
      'An item has attributes assigned and is ready for evaluation'
    ],
    seeAlso: ['Item Pool', 'Priority', 'Lifecycle Stage', 'Structuring', 'Attribute', 'Value']
  },
  {
    term: 'Priority',
    definition: 'Contextual: an item that has been placed in an item pool (list) with a rank. Priority is list membership plus rank, not a lifecycle state. Items in a pool have a position, cut line, and may have slottingRationale.',
    category: 'concept',
    examples: [
      'This item is at the top of our quarterly priority list',
      'Priorities are items in a list, ranked and above or below the cut line'
    ],
    seeAlso: ['Item', 'Item Pool', 'Cut Line', 'Above the Line', 'Slotting']
  },
  {
    term: 'Lifecycle State',
    definition: 'Item lifecycle. The system uses a single lifecycle state (item). Priority is determined by context: membership in an item pool and rank, not by a separate state.',
    category: 'concept',
    seeAlso: ['Item', 'Item Pool', 'Lifecycle Stage']
  },
  {
    term: 'Lifecycle Transition Path',
    definition: 'The progression through stages: Intake → Structuring → Ranking → Execution. Items gain attributes and values; when placed in a pool with a rank they are priorities in that context.',
    category: 'concept',
    seeAlso: ['Lifecycle Stage', 'Intake', 'Structuring', 'Ranking', 'Item', 'Item Pool']
  },

  // ============================================
  // Lifecycle Stages
  // ============================================
  {
    term: 'Intake',
    definition: 'The moment when an item first enters the prioritization system. May originate as a request, idea, observation, or issue. Should be lightweight and non-frictional—capture without judgment. Can occur through tools, meetings, conversation, or direct entry. Kicks off the structuring process but does not guarantee progression.',
    category: 'concept',
    examples: [
      'If it\'s not captured in Intake, it\'s invisible to the system—no visibility, no structuring, no priority'
    ],
    seeAlso: ['Item', 'Structuring', 'Lifecycle Stage']
  },
  {
    term: 'Structuring',
    definition: 'The process of clarifying an item: assigning attributes, articulating scope or intent. Makes an item evaluatable by others and ready for inclusion in an item pool. May be done by the originator, an item manager, or collaboratively during review. Structuring should be light but sufficient—not perfection, but clarity.',
    category: 'concept',
    examples: [
      'We need to structure this item before we can compare it to anything',
      'Structuring involves assigning attributes and clarifying scope'
    ],
    seeAlso: ['Item', 'Item Manager', 'Attribute', 'Structuring Completeness']
  },
  {
    term: 'Ranking',
    definition: 'The workflow stage where items are evaluated, compared, and positioned in item pools. Items in a pool with a rank are priorities (contextual). Involves scoring, slotting, and establishing relative order.',
    category: 'concept',
    seeAlso: ['Item', 'Priority', 'Item Pool', 'Slotting', 'Scoring', 'Lifecycle Stage']
  },
  {
    term: 'Execution',
    definition: 'The workflow stage where priorities are acted upon. Published Priority Lists guide work, and drift detection monitors alignment between declared priorities and actual work.',
    category: 'concept',
    seeAlso: ['Priority', 'Item Pool', 'Published Priority List', 'Focus Drift', 'Lifecycle Stage']
  },
  {
    term: 'Lifecycle Stage',
    definition: 'The workflow stage an item is in. Four stages: intake, structuring, ranking, execution. Tracks readiness, hand-offs, and dependencies; manages work-in-motion.',
    category: 'concept',
    seeAlso: ['Intake', 'Structuring', 'Ranking', 'Execution', 'Lifecycle State']
  },

  // ============================================
  // Structuring & Attributes
  // ============================================
  {
    term: 'Attribute',
    definition: 'A descriptive characteristic used to define or differentiate an item. Often applies across many items (e.g., effort, impact, risk, owner, theme). Enables structured thinking, tagging, and filtering. Attributes are usually assigned during the structuring phase when an item is being prepared. Some attributes may also serve as criteria when used for evaluation.',
    category: 'concept',
    examples: [
      'We added "customer visibility" as an attribute to help surface which items are externally obvious but internally neglected',
      'Attributes include: text, number, select, multiselect, date, boolean'
    ],
    seeAlso: ['Value', 'Criteria', 'Structuring', 'Item']
  },
  {
    term: 'Value',
    definition: 'The specific expression or selection assigned to an attribute for a given item. Can be quantitative (e.g., 5 hours, $10K, 3 FTEs) or qualitative (e.g., high, medium, low). Needed to make an item evaluatable and comparable. Becomes especially important when using the attribute as a criterion. Helps distinguish otherwise similar items in the prioritization process.',
    category: 'concept',
    examples: [
      'Both items were tagged with the same attribute set, but the value for "risk" was significantly higher on the second one, so it rose in the Item Pool'
    ],
    seeAlso: ['Attribute', 'Criteria', 'Scoring', 'Item']
  },
  {
    term: 'Criteria',
    definition: 'The standards, measures, characteristics, or dimensions that permit assignment of values to an item, allowing it to be compared and contrasted with others in a meaningful way. They essentially define what is important or valuable.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Attribute', 'Item Pool', 'Comparison Set', 'Criteria Authority', 'Scoring'],
    alternateDefinitions: [
      {
        definition: 'The explicit dimensions used to evaluate and compare items for prioritization. Often include attributes such as impact, effort, urgency, risk, strategic fit, and confidence. Must be defined clearly enough that items can be assessed relative to each other. Provide the basis for structured conversation, ranking, and tradeoff decisions. Can be applied differently across teams or domains, but must be shared within a given Item Pool. Defined at the Criteria Set level. Must be shared by all Items in a given Comparison Set. Governed by the Criteria Authority.',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: 'Scoring',
    definition: 'The act of assigning values to an item\'s attributes—usually in service of evaluation. May be subjective (e.g., rough sizing) or data-informed (e.g., revenue impact). Not required for prioritization, but often helpful for structured tradeoff discussions. Can occur individually or in a group, manually or via a rubric. Should align with the criteria defined for that Item Pool.',
    category: 'technique',
    examples: [
      'We\'re not scoring for precision—we\'re scoring for conversation'
    ],
    seeAlso: ['Attribute', 'Value', 'Criteria', 'Item']
  },
  {
    term: 'Framing',
    definition: 'The act of defining the scope, criteria, and comparison set for an Item Pool. Establishes the logic and context for how items will be evaluated and ranked. Often includes identifying stakeholders, constraints, and intended outcomes. Prevents apples-to-oranges comparisons and supports decision integrity. Should be revisited periodically as context changes.',
    category: 'technique',
    examples: [
      'Before we prioritize, let\'s frame the list—what type of work are we comparing, and why?'
    ],
    seeAlso: ['Item Pool', 'Comparison Set', 'Criteria', 'Criteria Authority']
  },
  {
    term: 'Slotting',
    definition: 'The act of placing an item into a specific ranked position in an Item Pool. Can be done manually (via discussion) or based on scoring/output. Acknowledges that priority is not just "high/medium/low"—it\'s relative. May involve moving other items up or down the list. Confirms that an item has become a priority and found its place.',
    category: 'technique',
    examples: [
      'We agreed it\'s a priority, but we haven\'t slotted it yet—where does it fall in the sequence?'
    ],
    seeAlso: ['Priority', 'Item Pool', 'Ranking', 'Cut Line']
  },
  {
    term: 'Structuring Completeness',
    definition: 'A percentage (0-100%) indicating how complete the structuring process is for an item. Calculated based on how many required attributes have been assigned values. Used to determine if an item meets prerequisites for ranking.',
    category: 'concept',
    seeAlso: ['Structuring', 'Item', 'Prerequisites', 'Attribute']
  },
  {
    term: 'Meets Prerequisites',
    definition: 'A boolean flag indicating whether an item has been sufficiently structured and meets the prerequisites for Item Prioritization. Typically set when structuring completeness reaches a threshold (e.g., 80%) and all required criteria have values.',
    category: 'concept',
    seeAlso: ['Item', 'Structuring Completeness', 'Criteria']
  },
  {
    term: 'Item Manager',
    definition: 'The person accountable for turning an intake item into a usable Item with proper item category and altitude assignment. Owns clarity, candidate type, altitude assignment, and alternate representations. May act as a delegate, point of contact, or shepherd—not necessarily the requester or decision-maker. Ensures the item is well-formed enough to be compared within its Comparison Cell.',
    category: 'concept',
    examples: [
      'That\'s a great idea—but it needs an item manager if we want to move it toward priority',
      'We\'ve collected 74 items in the backlog. Most still need structuring.'
    ],
    seeAlso: ['Structuring', 'Item', 'List Owner', 'Comparison Cell', 'Clusterer'],
    alternateDefinitions: [
      {
        definition: 'The person responsible for structuring and preparing an Item so that it can be evaluated. Sets item category, altitude, and assigns values. Primary Spaces: Item Intake, Item Structuring, Attribute Management.',
        context: 'Role definition in the prioritization system'
      },
      {
        definition: 'Responsible for the active structuring role.',
        context: 'Term mapping'
      }
    ]
  },

  // ============================================
  // Item Pool Structure (v2.2: Item Pool = canonical; Item Pool deprecated)
  // ============================================
  {
    term: 'Item Pool',
    definition: 'The comparison set selected during Arrange. A container of ordered items (priorities in context). Items in a pool with a rank are priorities for that list. Membership must be locked before ranking begins.',
    category: 'concept',
    seeAlso: ['Priority', 'Above the Line', 'Cut Line', 'Published Priority List']
  },
  {
    term: 'Item Pool (deprecated)',
    definition: 'DEPRECATED: Use Item Pool or Published Priority List. A legacy term for a container of ordered Priorities. See Item Pool for the canonical term.',
    category: 'concept',
    examples: [
      'Let\'s pull up the Item Pool for infrastructure—what\'s above the line this cycle, and what got bumped?'
    ],
    seeAlso: ['Item Pool', 'Published Priority List', 'Priority', 'Cut Line']
  },
  {
    term: 'Cut Line',
    definition: 'The dynamic threshold separating ranked priorities that are in scope from those that are out of scope. Visual threshold (dashed red line with scissors icon ✂️). Separates active work from deferred work. Can be moved up or down based on capacity. Movement across the cut line should prompt structured conversation. Essential for enabling flexibility without re-litigating rankings.',
    category: 'concept',
    examples: [
      'I know it\'s ranked high, but it fell below the cut line when we lost that headcount.'
    ],
    seeAlso: ['Item Pool', 'Above the Line', 'Below the Line', 'Priority']
  },
  {
    term: 'Above the Line',
    definition: 'The portion of an Item Pool that reflects active, committed priorities. Indicates which items are currently resourced and intended to be acted upon. May shift based on constraints, planning cycles, or leadership review. Creates an explicit boundary between what is in play and what is deferred. Supports clear communication and tradeoff reasoning.',
    category: 'concept',
    examples: [
      'If it\'s not above the line, we\'re not doing it this cycle—no matter how important it feels.'
    ],
    seeAlso: ['Item Pool', 'Cut Line', 'Below the Line', 'Priority']
  },
  {
    term: 'Below the Line',
    definition: 'Items in an Item Pool that went through item work and evaluation but did not make the current cut. Retained for visibility, traceability, and future review. May become relevant again as capacity shifts or priorities change. Helps teams avoid memory loss or siloed decision-making. Should not be confused with "backlog"—these are ranked but currently deprioritized.',
    category: 'concept',
    examples: [
      'It\'s below the line for now—but it\'s still visible if something drops out or bandwidth opens up.'
    ],
    seeAlso: ['Item Pool', 'Cut Line', 'Above the Line', 'Priority']
  },
  {
    term: 'Focus Map',
    definition: 'A structured collection of all active Item Pools across the organization. Provides a multi-dimensional picture of what matters most across teams, domains, and time horizons. Used to resolve cross-functional conflicts, inform dynamic replanning, and maintain strategic coherence. Enables decentralized action without compromising unified intent. Continuously updated as priorities shift or new items are elevated. Contains multiple Item Pools, supports filtering and views by Dimension, is navigated by Cross-Map Leads and others during realignment, enables detection of Focus Drift.',
    category: 'concept',
    examples: [
      'If it\'s not on the Focus Map—or if it\'s below the line somewhere—it\'s not where we\'re spending energy right now.'
    ],
    seeAlso: ['Item Pool', 'Dimension', 'Cross-Map Lead', 'Focus Drift']
  },
  {
    term: 'Comparison Set',
    definition: 'A group of like-kind items being evaluated relative to one another. Defined by shared characteristics or evaluation criteria. Determines the legitimate scope of a single Item Pool. Ensures apples-to-apples prioritization and prevents muddled rankings. Comparison sets may differ across teams, cycles, or strategic lenses.',
    category: 'concept',
    examples: [
      'We\'re not comparing product launches to tech debt—each belongs in its own comparison set.'
    ],
    seeAlso: ['Item Pool', 'Criteria', 'Framing']
  },
  {
    term: 'Dimension',
    definition: 'A grouping or axis along which multiple items can be evaluated, compared, or filtered. Often used when viewing many Item Pools together across a Focus Map. Examples include: domain (e.g., product, infrastructure), time horizon (e.g., near, mid, long), objective, or strategic pillar. Dimensions can guide prioritization structure or reporting views. Important for building systems that scale across teams, not just within them.',
    category: 'concept',
    examples: [
      'We\'re slicing the Focus Map by time horizon—so now we\'re looking at priorities along the "time" dimension only.'
    ],
    seeAlso: ['Focus Map', 'Item Pool', 'Focus Horizon']
  },

  // ============================================
  // Focus Horizons
  // ============================================
  {
    term: 'Focus Horizon',
    definition: 'Describes how long an Item Pool or Item is expected to remain valid. Applied to Items and each Item Pool (e.g., "Q2," "Annual Strategy"). Informs review cadence and expectation of change. Used to determine when realignment or reprioritization is triggered. Helps define the intended lifespan of an Item Pool or Item context. Anchors prioritization decisions in a meaningful timeframe.',
    category: 'concept',
    examples: [
      'This might matter a lot—but not on this Focus Horizon. Let\'s park it for the next cycle.'
    ],
    seeAlso: ['Item Pool', 'Reprioritization Trigger', 'Short-term', 'Quarterly', 'Annual', 'Strategic']
  },
  {
    term: 'Short-term',
    definition: 'Weekly or monthly focus horizon.',
    category: 'concept',
    seeAlso: ['Focus Horizon', 'Quarterly', 'Mid-term', 'Annual', 'Long-term', 'Strategic']
  },
  {
    term: 'Quarterly',
    definition: '3-month planning cycles horizon.',
    category: 'concept',
    seeAlso: ['Focus Horizon', 'Short-term', 'Mid-term', 'Annual', 'Long-term', 'Strategic']
  },
  {
    term: 'Mid-term',
    definition: '6-month horizons.',
    category: 'concept',
    seeAlso: ['Focus Horizon', 'Short-term', 'Quarterly', 'Annual', 'Long-term', 'Strategic']
  },
  {
    term: 'Annual',
    definition: 'Year-long strategic initiatives horizon.',
    category: 'concept',
    seeAlso: ['Focus Horizon', 'Short-term', 'Quarterly', 'Mid-term', 'Long-term', 'Strategic']
  },
  {
    term: 'Long-term',
    definition: 'Extended planning horizon.',
    category: 'concept',
    seeAlso: ['Focus Horizon', 'Short-term', 'Quarterly', 'Mid-term', 'Annual', 'Strategic']
  },
  {
    term: 'Strategic',
    definition: 'Multi-year vision work horizon.',
    category: 'concept',
    seeAlso: ['Focus Horizon', 'Short-term', 'Quarterly', 'Mid-term', 'Annual', 'Long-term']
  },

  // ============================================
  // Focus Drift & Realignment
  // ============================================
  {
    term: 'Focus Drift',
    definition: 'Occurs when a team or individual\'s behavior begins to diverge from their declared Item Pool. Can result from outdated rankings, shifting conditions, or unspoken re-prioritization. Often subtle at first—work gets done, but not in alignment with agreed focus. Naming it enables course correction without blame. Healthy systems regularly scan for and reconcile drift.',
    category: 'concept',
    examples: [
      'This isn\'t a process failure—it\'s just Focus Drift. Let\'s recalibrate before it compounds.'
    ],
    seeAlso: ['Item Pool', 'Focus Realignment', 'Drift Event', 'Stale Priority']
  },
  {
    term: 'Focus Realignment',
    definition: 'The structured process of updating Item Pools or the Focus Map to match current conditions. May involve re-framing, restructuring, re-ranking, or resolving conflicting lists. Should include cross-functional input when dependencies are affected. Helps prevent drift from calcifying into chaos. Ideally happens at predictable intervals or immediately after a reprioritization trigger.',
    category: 'technique',
    examples: [
      'We\'re not starting over—we\'re realigning based on what\'s true now.'
    ],
    seeAlso: ['Focus Drift', 'Reprioritization Trigger', 'Item Pool', 'Focus Map']
  },
  {
    term: 'Reprioritization Trigger',
    definition: 'A condition or event that signals it\'s time to revisit a Item Pool or Map. Can be explicit (e.g., missed milestone, resource change) or emergent (e.g., new opportunity, executive shift). Helps teams adjust with intention instead of reacting ad hoc. Triggers should be discussed and agreed upon in advance whenever possible. May lead to partial restructuring or a full re-framing process.',
    category: 'concept',
    examples: [
      'This deal acceleration is a valid reprioritization trigger—let\'s review the customer-impact list this week.'
    ],
    seeAlso: ['Focus Realignment', 'Focus Drift', 'Item Pool']
  },
  {
    term: 'Stale Priority',
    definition: 'An item that remains on a Item Pool but is no longer accurate, relevant, or active. Can result from context shifts, deprioritized initiatives, or incomplete structuring. Degrades trust in the list and adds invisible decision drag. Should be reviewed, archived, or updated to reflect current reality. May indicate a need for better review cadence or ownership clarity.',
    category: 'anti-pattern',
    examples: [
      'Half this list is stale—we can\'t make good tradeoffs if we don\'t clean this up.'
    ],
    seeAlso: ['Item Pool', 'Focus Drift', 'Priority']
  },
  {
    term: 'Drift Event',
    definition: 'A detected instance of Focus Drift. Contains information about when it was detected, what type of drift it is, severity level, affected priorities, and resolution status.',
    category: 'concept',
    seeAlso: ['Focus Drift', 'Stale Priority']
  },

  // ============================================
  // Roles & Permissions
  // ============================================
  {
    term: 'List Owner',
    definition: 'The person accountable for the clarity, accuracy, and integrity of a specific Item Pool. Ensures items are ready, rankings are updated, and tradeoffs are documented. May consult with others but is responsible for final articulation. Helps prevent stagnation, bloat, or drift in priority systems. May shift by quarter, initiative, or role structure. Primary Spaces: Item Pool Creation & Framing, Item Pool Editing & Cut Line Management, Drift Detection, Realignment.',
    category: 'concept',
    examples: [
      'Every team has a List Owner—it\'s their job to make sure we can actually use the list to decide things.'
    ],
    seeAlso: ['Item Pool', 'Item Manager', 'Criteria Authority', 'Focus Facilitator']
  },
  {
    term: 'Criteria Authority',
    definition: 'Defines the evaluative logic (i.e., what counts as a good comparison) for a Item Pool or group of lists. Create/edit criteria, approve list framing, define scoring standards. Stand up a new Item Pool, change evaluation model, detect criteria drift. Primary Spaces: Attribute Management, Criteria Selection & Comparison Set Framing, Item Pool Creation (initial setup).',
    category: 'concept',
    examples: [
      'We\'re not just guessing—each item is scored against the same three criteria: customer impact, engineering complexity, and time sensitivity.'
    ],
    seeAlso: ['Criteria', 'Item Pool', 'Framing', 'List Owner']
  },
  {
    term: 'Focus Facilitator',
    definition: 'Guides a team through structured prioritization conversations, often during live sessions. Moderate sessions, enforce framing boundaries, pause/lock edits. Prioritization session, multi-role meeting, conflict resolution. Primary Spaces: Prioritization Session Support, Item Pool Editing (viewing during sessions), Realignment. This role enforces the process, not the outcome; must be neutral and fluent in the framework.',
    category: 'concept',
    seeAlso: ['Item Pool', 'Prioritization Session', 'List Owner']
  },
  {
    term: 'Cross-Map Lead',
    definition: 'Oversees coherence across multiple Item Pools, domains, or teams. Compare lists, flag misalignments, convene multi-list reviews. Quarterly rebalancing, shared resource tension, portfolio drift. Primary Spaces: Focus Map Navigation & Visualization, Drift Detection, Realignment, Analytics Layer. Often a director/exec or senior ops role; should have visibility across domains and authority to intervene.',
    category: 'concept',
    examples: [
      'Often a director/exec or senior ops role; should have visibility across domains and authority to intervene.'
    ],
    seeAlso: ['Focus Map', 'Item Pool', 'Focus Drift', 'Focus Realignment']
  },
  {
    term: 'Strategist',
    definition: 'Interprets patterns, drift, and cycle health to improve systemic focus. View system metrics, tag concerns, propose improvements. High drift rates, stale priorities, structuring bottlenecks. Primary Spaces: Analytics & Insight Layer, Realignment (read-only), Governance Layer (read-only). May evolve into a distinct "Focus Coach" or "Prioritization Ops" role over time.',
    category: 'concept',
    seeAlso: ['Analytics', 'Focus Drift', 'Pattern']
  },
  {
    term: 'Permission Tier',
    definition: 'A numeric level (0-5) that defines what actions a user can perform in the system. Tier 0 (Observer): Can view system data and activity, but not interact. Tier 1 (Contributor): Can create items and comment, but cannot change rankings or framing. Tier 2 (Editor): Can shape items, assign attributes, and propose rankings. Tier 3 (Framing Authority): Can define comparison sets, criteria, and cut lines. Tier 4 (Prioritization Authority): Can finalize rankings and publish cut lines. Tier 5 (System Admin): Can manage governance settings, roles, policies, and integrations.',
    category: 'concept',
    seeAlso: ['List Owner', 'Item Manager', 'Criteria Authority', 'Actor Role']
  },
  {
    term: 'Actor Role',
    definition: 'One of six core roles in the prioritization system: List Owner, Item Manager, Criteria Authority, Focus Facilitator, Cross-Map Lead, or Strategist. Each role has specific responsibilities and access to different System Modeling Spaces.',
    category: 'concept',
    seeAlso: ['List Owner', 'Item Manager', 'Criteria Authority', 'Focus Facilitator', 'Cross-Map Lead', 'Strategist', 'Permission Tier']
  },

  // ============================================
  // System Architecture
  // ============================================
  {
    term: 'Architecture Layer',
    definition: 'A grouping of System Modeling Spaces by platform function. Six layers organize the 12 spaces: Input Layer (Spaces 1-2), Structure Layer (Spaces 3-5), Action Layer (Spaces 6-7), Execution Layer (Spaces 8-9), System Coordination Layer (Spaces 10-11), Measurement & Optimization Layer (Space 12).',
    category: 'concept',
    seeAlso: ['System Modeling Space', 'Input Layer', 'Structure Layer', 'Action Layer', 'Execution Layer']
  },
  {
    term: 'System Modeling Space',
    definition: 'One of 12 distinct functional zones of the prioritization platform. Each space represents a specific area of functionality within the system: 1. Item Intake & Classification, 2. Item Structuring, 3. Attribute Management, 4. Criteria Selection & Comparison Set Framing, 5. Item Pool Creation & Framing, 6. Item Pool Editing & Cut Line Management, 7. Prioritization Session Support, 8. Above-the-Line Enforcement & Drift Detection, 9. Focus Realignment & Change Reconciliation, 10. Focus Map Navigation & Visualization, 11. System Settings & Governance Layer, 12. Analytics & Insight Layer.',
    category: 'concept',
    seeAlso: ['Architecture Layer', 'Item Intake', 'Item Structuring', 'Item Pool']
  },

  // ============================================
  // Prioritization Tools & Methods
  // ============================================
  {
    term: 'Prioritization Tool',
    definition: 'A predefined configuration that defines required attributes (for Space 3: Attribute Management) and provides default criteria selection and weights (for Space 4: Criteria Selection). Includes guidance for best practices, when to use, and when not to use.',
    category: 'framework',
    seeAlso: ['RICE Framework', 'ICE Framework', 'WSJF', 'Impact-Effort Matrix', 'Eisenhower Matrix']
  },
  {
    term: 'RICE Framework',
    definition: 'Reach, Impact, Confidence, Effort - A popular prioritization framework for product teams. Calculates RICE score = (Reach × Impact × Confidence) / Effort. Category: strategic. Complexity: moderate.',
    category: 'framework',
    examples: [
      'Feature prioritization for product roadmap',
      'Team backlog management'
    ],
    seeAlso: ['ICE Framework', 'WSJF', 'Prioritization Tool']
  },
  {
    term: 'ICE Framework',
    definition: 'Impact, Confidence, Effort - A simplified prioritization framework. Calculates ICE score = (Impact × Confidence) / Effort. Category: strategic. Complexity: simple.',
    category: 'framework',
    seeAlso: ['RICE Framework', 'WSJF', 'Prioritization Tool']
  },
  {
    term: 'WSJF',
    definition: 'Weighted Smallest Job First - A sorting technique often used in Agile environments that ranks items by dividing their Cost of Delay by the job duration, prioritizing work that delivers the most value in the shortest time.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'framework',
    seeAlso: ['RICE Framework', 'ICE Framework', 'Cost of Delay', 'Prioritization Tool', 'Weighted Smallest Job First'],
    alternateDefinitions: [
      {
        definition: 'Weighted Shortest Job First - Cost of Delay divided by Duration. Optimizes for business value delivery. Category: strategic. Complexity: complex.',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: 'Impact-Effort Matrix',
    definition: 'A decision-making framework/tool used to prioritize work, projects, or tasks based on the estimated impact (value) they will have versus the estimated effort required for their implementation.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Eisenhower Matrix', 'Prioritization Tool'],
    alternateDefinitions: [
      {
        definition: 'A 2x2 matrix categorizing items by Impact (High/Low) and Effort (High/Low). Four quadrants: Quick Wins (High Impact, Low Effort), Major Projects (High Impact, High Effort), Fill-ins (Low Impact, Low Effort), Thankless Tasks (Low Impact, High Effort). Category: categorization. Complexity: simple.',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: 'Eisenhower Matrix',
    definition: 'A general-purpose visual framework that relies on the independent dimensions of urgency and importance to quickly determine whether an item needs immediate attention or can tolerate a delay in attention or action.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Impact-Effort Matrix', 'Prioritization Tool', 'Urgency'],
    alternateDefinitions: [
      {
        definition: 'A 2x2 matrix categorizing items by Urgency (Urgent/Not Urgent) and Importance (Important/Not Important). Four quadrants: Do First (Urgent & Important), Schedule (Not Urgent & Important), Delegate (Urgent & Not Important), Eliminate (Not Urgent & Not Important). Category: categorization. Complexity: simple.',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: 'MoSCoW Method',
    definition: 'Categorizes items into Must Have, Should Have, Could Have, Won\'t Have. Category: categorization. Complexity: simple.',
    category: 'technique',
    seeAlso: ['Prioritization Tool']
  },
  {
    term: 'Stack Ranking',
    definition: 'The most basic qualitative sorting technique that evaluates a set of items based on implicit or explicit criteria and orders them sequentially from the most important to the least important, forcing tough choices when items appear equal in value.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Pairwise Ranking', 'Prioritization Tool', 'Paired Comparison'],
    alternateDefinitions: [
      {
        definition: 'A comparison method where items are ranked in a strict order from highest to lowest priority. Category: comparison. Complexity: moderate.',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: 'Pairwise Ranking',
    definition: 'A comparison method where items are compared two at a time to determine relative priority. Category: comparison. Complexity: moderate.',
    category: 'technique',
    seeAlso: ['Stack Ranking', 'Prioritization Tool']
  },
  {
    term: 'Dot Voting',
    definition: 'A prioritization simulation/method where participants allocate a fixed number of nonbinding votes ("dots") to various items, quickly gauging the collective preference of a group.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Buy a Feature', 'Prioritization Tool', 'Simulations'],
    alternateDefinitions: [
      {
        definition: 'A voting/simulation method where participants allocate votes (dots) to items. Category: voting. Complexity: simple.',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: 'Buy a Feature',
    definition: 'A simulation/innovation game where stakeholders use a set amount of "money" to purchase features or other elements they believe are most important, thereby forcing them to make trade-offs.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Dot Voting', 'Prioritization Tool', 'Simulations'],
    alternateDefinitions: [
      {
        definition: 'A voting/simulation method where participants allocate a budget to "buy" features. Category: voting. Complexity: moderate.',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: 'Cost of Delay',
    definition: 'A method used to assess the cost, monetary or otherwise, of deferring attention, a decision, or action on an item. The formula is: Value Lost Due to Delay + Cost to Restart or Rework.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'framework',
    seeAlso: ['WSJF', 'Cost of Delay Thinking', 'Prioritization Tool'],
    alternateDefinitions: [
      {
        definition: 'A framework for evaluating the downstream consequences of waiting. Category: strategic. Complexity: moderate.',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: 'Pareto Analysis',
    definition: 'A simple, universally applicable sorting method that suggests roughly 20% of the causes typically create approximately 80% of the effects. It helps you focus on the most significant items in a set.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Prioritization Tool', '80/20 rule'],
    alternateDefinitions: [
      {
        definition: 'A method for identifying the 20% of items that deliver 80% of value. Category: scoring. Complexity: moderate.',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: 'Simple Weighted Scoring',
    definition: 'A sorting technique that uses basic math to calculate priorities by assigning percentage weights to different criteria and scoring options against them, making the prioritization process systematic, transparent, and repeatable for teams.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Scoring', 'Prioritization Tool', 'RICE'],
    alternateDefinitions: [
      {
        definition: 'A scoring method where items are scored on multiple criteria with weights applied. Category: scoring. Complexity: moderate.',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: 'Max Priorities Pyramid',
    definition: 'A general-purpose framework utilizing Stack Ranking across a vertical time axis (future to now) and horizontal categories, designed to visualize relationships among competing priorities and align tactical execution with strategic goals.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Prioritization Tool', 'Stack Ranking'],
    alternateDefinitions: [
      {
        definition: 'A pyramid structure limiting the number of priorities at each level. Category: categorization. Complexity: simple.',
        context: 'Peak Priorities System definition'
      }
    ]
  },

  // ============================================
  // 12 Capabilities
  // ============================================
  {
    term: '12 Capabilities Model',
    definition: 'Comprehensive model of prioritization competencies across four families. The 12 capabilities are organized into: Contextual Awareness Family (1-3), Execution Rhythms Family (4-6), Relational Navigation Family (7-9), and Motivational Activation Family (10-12).',
    relatedFramework: 'pf.12.capabilities',
    category: 'framework',
    seeAlso: ['Capability', 'Capability Family', 'Situational Awareness', 'Conceptual Precision', 'Cost of Delay Thinking', 'Rhythm & Ritual', 'Process Discipline', 'Tool Fluency', 'Stakeholder Intelligence', 'Cultural Reinforcement', 'Tradeoff Intelligence', 'Clarity of Focus', 'Avoidance & Activation', 'Outcome Reflection']
  },
  {
    term: 'Capability',
    definition: 'One of 12 prioritization competencies organized into four families. Each capability represents a specific skill or competency in prioritization.',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Capability Family', 'Capability Score']
  },
  {
    term: 'Situational Awareness',
    definition: 'Recognizing when prioritization is needed. Part of the Contextual Awareness Family (Capability 1).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Contextual Awareness', 'Conceptual Precision', 'Cost of Delay Thinking']
  },
  {
    term: 'Conceptual Precision',
    definition: 'Avoiding false equivalencies and building language clarity. Part of the Contextual Awareness Family (Capability 2).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Contextual Awareness', 'Situational Awareness', 'Cost of Delay Thinking']
  },
  {
    term: 'Cost of Delay Thinking',
    definition: 'Evaluating downstream consequences of waiting. Part of the Contextual Awareness Family (Capability 3).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Contextual Awareness', 'Cost of Delay', 'Situational Awareness', 'Conceptual Precision']
  },
  {
    term: 'Rhythm & Ritual',
    definition: 'Establishing reliable cadence for revisiting priorities. Part of the Execution Rhythms Family (Capability 4).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Execution Rhythms', 'Process Discipline', 'Tool Fluency']
  },
  {
    term: 'Process Discipline',
    definition: 'Using structured, repeatable methods like DEGAP. Part of the Execution Rhythms Family (Capability 5).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Execution Rhythms', 'DEGAP Process Model', 'Rhythm & Ritual', 'Tool Fluency']
  },
  {
    term: 'Tool Fluency',
    definition: 'Effectively using appropriate prioritization tools. Part of the Execution Rhythms Family (Capability 6).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Execution Rhythms', 'Prioritization Tool', 'Process Discipline', 'Rhythm & Ritual']
  },
  {
    term: 'Stakeholder Intelligence',
    definition: 'Understanding who\'s affected and their needs. Part of the Relational Navigation Family (Capability 7).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Relational Navigation', 'Cultural Reinforcement', 'Tradeoff Intelligence']
  },
  {
    term: 'Cultural Reinforcement',
    definition: 'Modeling prioritization as valued behavior. Part of the Relational Navigation Family (Capability 8).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Relational Navigation', 'Stakeholder Intelligence', 'Tradeoff Intelligence']
  },
  {
    term: 'Tradeoff Intelligence',
    definition: 'Navigating legitimate tensions between priorities. Part of the Relational Navigation Family (Capability 9).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Relational Navigation', 'Stakeholder Intelligence', 'Cultural Reinforcement']
  },
  {
    term: 'Clarity of Focus',
    definition: 'Understanding what is being prioritized and why. Part of the Motivational Activation Family (Capability 10).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Motivational Activation', 'Avoidance & Activation', 'Outcome Reflection']
  },
  {
    term: 'Avoidance & Activation',
    definition: 'Surfacing and addressing avoided work. Part of the Motivational Activation Family (Capability 11).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Motivational Activation', 'Clarity of Focus', 'Outcome Reflection']
  },
  {
    term: 'Outcome Reflection',
    definition: 'Assessing whether past choices led to meaningful results. Part of the Motivational Activation Family (Capability 12).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Motivational Activation', 'Clarity of Focus', 'Avoidance & Activation']
  },

  // ============================================
  // Capability Families
  // ============================================
  {
    term: 'Capability Family',
    definition: 'One of four coherent domains that organize the 12 capabilities: Contextual Awareness (Capabilities 1,2,3) – Recognizing when prioritization is needed, understanding what it really means, building awareness of consequences. Execution Rhythms (Capabilities 4,5,6) – Building consistent habits and processes, enabling intentional rather than reactive prioritization, developing methodological competence. Relational Navigation (Capabilities 7,8,9) – Managing social, political, and human realities, building shared understanding and sustained commitment, navigating competing stakeholder needs. Motivational Activation (Capabilities 10,11,12) – Personal and collective discipline to act on what matters, confronting avoidance and learning from outcomes, converting clarity into meaningful progress.',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['12 Capabilities Model', 'Contextual Awareness', 'Execution Rhythms', 'Relational Navigation', 'Motivational Activation']
  },
  {
    term: 'Contextual Awareness',
    definition: 'Capability Family (1-3): Recognizing when prioritization is needed, understanding what it really means, building awareness of consequences. Includes: Situational Awareness, Conceptual Precision, Cost of Delay Thinking.',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['Capability Family', 'Situational Awareness', 'Conceptual Precision', 'Cost of Delay Thinking']
  },
  {
    term: 'Execution Rhythms',
    definition: 'Capability Family (4-6): Building consistent habits and processes, enabling intentional rather than reactive prioritization, developing methodological competence. Includes: Rhythm & Ritual, Process Discipline, Tool Fluency.',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['Capability Family', 'Rhythm & Ritual', 'Process Discipline', 'Tool Fluency']
  },
  {
    term: 'Relational Navigation',
    definition: 'Capability Family (7-9): Managing social, political, and human realities, building shared understanding and sustained commitment, navigating competing stakeholder needs. Includes: Stakeholder Intelligence, Cultural Reinforcement, Tradeoff Intelligence.',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['Capability Family', 'Stakeholder Intelligence', 'Cultural Reinforcement', 'Tradeoff Intelligence']
  },
  {
    term: 'Motivational Activation',
    definition: 'Capability Family (10-12): Personal and collective discipline to act on what matters, confronting avoidance and learning from outcomes, converting clarity into meaningful progress. Includes: Clarity of Focus, Avoidance & Activation, Outcome Reflection.',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['Capability Family', 'Clarity of Focus', 'Avoidance & Activation', 'Outcome Reflection']
  },

  // ============================================
  // Capability Orientations
  // ============================================
  {
    term: 'Orientation',
    definition: 'A grouping of capabilities that share a common focus: Self-Oriented (Contextual Awareness + Motivational Activation: 1,2,3,10,11,12), System-Oriented (Execution Rhythms + Relational Navigation: 4,5,6,7,8,9), Sense & Align (Contextual Awareness + Relational Navigation: 1,2,3,7,8,9), Activate & Execute (Motivational Activation + Execution Rhythms: 4,5,6,10,11,12).',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['Self-Oriented', 'System-Oriented', 'Sense & Align', 'Activate & Execute', 'Capability Family']
  },
  {
    term: 'Self-Oriented',
    definition: 'Orientation combining Contextual Awareness + Motivational Activation (Capabilities 1,2,3,10,11,12). Focuses on individual awareness and motivation.',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['Orientation', 'System-Oriented', 'Contextual Awareness', 'Motivational Activation']
  },
  {
    term: 'System-Oriented',
    definition: 'Orientation combining Execution Rhythms + Relational Navigation (Capabilities 4,5,6,7,8,9). Focuses on system-level processes and relationships.',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['Orientation', 'Self-Oriented', 'Execution Rhythms', 'Relational Navigation']
  },
  {
    term: 'Sense & Align',
    definition: 'Orientation combining Contextual Awareness + Relational Navigation (Capabilities 1,2,3,7,8,9). Focuses on understanding context and aligning stakeholders.',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['Orientation', 'Activate & Execute', 'Contextual Awareness', 'Relational Navigation']
  },
  {
    term: 'Activate & Execute',
    definition: 'Orientation combining Motivational Activation + Execution Rhythms (Capabilities 4,5,6,10,11,12). Focuses on motivation and execution processes.',
    relatedFramework: 'pf.12.capabilities',
    category: 'concept',
    seeAlso: ['Orientation', 'Sense & Align', 'Motivational Activation', 'Execution Rhythms']
  },

  // ============================================
  // Assessment & Scoring
  // ============================================
  {
    term: 'Assessment Question',
    definition: 'A question in the prioritization assessment that evaluates a specific capability through three lenses: Doing, Knowing, and Gap. Each question has 5 response options scored 1-5.',
    category: 'concept',
    seeAlso: ['Question Part', 'Response Option', 'Capability', 'Doing', 'Knowing', 'Gap']
  },
  {
    term: 'Question Part',
    definition: 'One of three parts of an assessment question: Doing (How consistently you practice this capability), Knowing (How well you understand this capability), Gap (The difference between your knowing and doing).',
    category: 'concept',
    seeAlso: ['Assessment Question', 'Doing', 'Knowing', 'Gap']
  },
  {
    term: 'Doing',
    definition: 'How consistently you practice this capability. One of three parts of an assessment question.',
    category: 'concept',
    seeAlso: ['Assessment Question', 'Question Part', 'Knowing', 'Gap']
  },
  {
    term: 'Knowing',
    definition: 'How well you understand this capability. One of three parts of an assessment question.',
    category: 'concept',
    seeAlso: ['Assessment Question', 'Question Part', 'Doing', 'Gap']
  },
  {
    term: 'Gap',
    definition: 'The difference between your knowing and doing. One of three parts of an assessment question.',
    category: 'concept',
    seeAlso: ['Assessment Question', 'Question Part', 'Doing', 'Knowing']
  },
  {
    term: 'Response Option',
    definition: 'One of five possible answers to an assessment question, scored 1-5: Score 1 (Rarely or never practice this), Score 2 (Practice occasionally), Score 3 (Practice sometimes), Score 4 (Practice regularly), Score 5 (Practice consistently and effectively).',
    category: 'concept',
    seeAlso: ['Assessment Question']
  },
  {
    term: 'Scope Level',
    definition: 'The level at which a question is asked: Individual (Personal prioritization), Team (Team-level prioritization), Organization (Organization-level prioritization).',
    category: 'concept',
    seeAlso: ['Assessment Question', 'Individual', 'Team', 'Organization']
  },
  {
    term: 'Individual',
    definition: 'Personal prioritization scope level.',
    category: 'concept',
    seeAlso: ['Scope Level', 'Team', 'Organization']
  },
  {
    term: 'Team',
    definition: 'Team-level prioritization scope level.',
    category: 'concept',
    seeAlso: ['Scope Level', 'Individual', 'Organization']
  },
  {
    term: 'Organization',
    definition: 'Organization-level prioritization scope level.',
    category: 'concept',
    seeAlso: ['Scope Level', 'Individual', 'Team']
  },
  {
    term: 'Capability Score',
    definition: 'The average of the three questions (Doing, Knowing, Gap) for that capability.',
    category: 'concept',
    seeAlso: ['Capability', 'Family Score', 'Overall Score', 'Assessment Question']
  },
  {
    term: 'Family Score',
    definition: 'The average of the three capability scores within that family.',
    category: 'concept',
    seeAlso: ['Capability Family', 'Capability Score', 'Overall Score']
  },
  {
    term: 'Overall Score',
    definition: 'The average of all 12 capability scores.',
    category: 'concept',
    seeAlso: ['Capability Score', 'Family Score', 'Orientation Score']
  },
  {
    term: 'Orientation Score',
    definition: 'The average of the 6 capabilities in each orientation (Self-Oriented or System-Oriented).',
    category: 'concept',
    seeAlso: ['Orientation', 'Overall Score', 'Capability Score']
  },
  {
    term: 'Pattern',
    definition: 'A detected pattern in assessment responses that indicates a specific prioritization profile or challenge. The system evaluates 33 patterns.',
    category: 'concept',
    seeAlso: ['Archetype', 'Assessment Question']
  },
  {
    term: 'Archetype',
    definition: 'A detected archetype in assessment responses that represents a specific prioritization style or approach. The system evaluates 15 archetypes.',
    category: 'concept',
    seeAlso: ['Pattern', 'Assessment Question']
  },
  {
    term: 'Progression Level',
    definition: 'A level of development for a capability: aware (Basic awareness), developing (Developing skill), proficient (Proficient application), mastery (Mastery level), integrative (Integrative application).',
    category: 'concept',
    seeAlso: ['Capability', 'Capability Score']
  },

  // ============================================
  // Knowledge Engine & Content
  // ============================================
  {
    term: 'Knowledge Object',
    definition: 'A content object in the knowledge engine that can be a Framework, Model, Tool, Distinction, Story, Card, Principle, or other type. Contains metadata, relationships, and content.',
    category: 'concept',
    seeAlso: ['Object Type', 'Object Subtype', 'Relationship Type', 'Structure', 'System']
  },
  {
    term: 'Object Type',
    definition: 'The category of a knowledge object. Valid types include: Framework, Model, Method, Methodology, Tool, Technique, Principle, Distinction, Taxonomy, Ontology, Schema, Structure, Value, Virtue, Lens, Perspective, Story, Narrative, Case, Scenario, Insight, Quote, Aphorism, Example, Resource, Checklist, Worksheet, Template, Assessment, Playbook, Guide, Glossary, Dictionary, Card, Micro-lesson, Ritual, Practice, Process, System, Doctrine, Exercise, Pattern, Visual, Micro-Content, Other.',
    category: 'concept',
    seeAlso: ['Knowledge Object', 'Object Subtype']
  },
  {
    term: 'Object Subtype',
    definition: 'A more specific classification of a knowledge object. Valid subtypes include: DoctrineCard, FieldCard, ReflectionCard, CanvasTemplate, ProcessFramework, ConceptualDistinction, Narrative, PromptSet, FacilitatorGuide, BlogDraft, None.',
    category: 'concept',
    seeAlso: ['Knowledge Object', 'Object Type']
  },
  {
    term: 'Relationship Type',
    definition: 'A type of connection between knowledge objects. Valid relationship types include: is-a, part-of, contains, applies-to, used-by, derived-from, inspired-by, analogous-to, contrasts-with, supports, supported-by, illustrates, explains, requires, enables, linked-to, references, refines, extends, replaces, precedes, follows, is-level-of, relates-to, maps-to, connected-to, attaches-to, is-example-of, is-variation-of, specializes, generalizes, depends-on, incompatible-with, translates-to.',
    category: 'concept',
    seeAlso: ['Knowledge Object']
  },
  {
    term: 'Structure',
    definition: 'How a knowledge object is organized internally. Valid structures include: Hierarchy/Tree, Network/Graph, Sequence/Ordered List, Unordered List/Collection/Set, Matrix (2D grid), Table, Flow/Process Flow/Swimlane, Cycle/Loop, Phased Process (Stepwise), Layered/Nested/Tiered, Continuum/Range/Spectrum, Toggle/Binary/Either–Or, Multi-State/Mode-Based, Map (literal or metaphorical), Checklist, Card Deck/Modular Units, Canvas (visual workspace), Spiral/Helix (cyclical progression), Taxonomy (classification structure), Ontology (network of types), Framework (used as structure), Tree Map/Sunburst, Faceted/Multi-Dimensional, Pyramid (hierarchy with emphasis), Constellation (visual network).',
    category: 'concept',
    seeAlso: ['Knowledge Object'],
    alternateDefinitions: [
      {
        definition: 'The Structure layer in the architecture that adds attributes, criteria, comparison sets (Spaces 3-5).',
        context: 'System Architecture'
      }
    ]
  },
  {
    term: 'System',
    definition: 'A larger whole that knowledge objects belong to. Valid systems include: Peak Priorities Knowledge System, DEGAP, Capability Model, Prioritization Assessment System, Personal Growth Framework, Leadership Toolkit, Compass Virtues, Coaching Toolkit, Workshop Toolkit, Resource Library, Assessment System, Glossary, Client Success Library, Content Object Library, Rituals & Practices Library, Product Development System, Change Management Toolkit, Communication System, Story Library, Meta-Frameworks Archive, Individual/Team/Org Maturity Models, Decision Architecture System, Playbook Library, Theme Park Maps, Innovation Toolkit.',
    category: 'concept',
    seeAlso: ['Knowledge Object'],
    alternateDefinitions: [
      {
        definition: 'The overall Peak Priorities platform/system.',
        context: 'Technical system'
      }
    ]
  },
  {
    term: 'Card',
    definition: 'A modular teaching unit in the knowledge system containing multiple atomic objects. Structure: Card, Matrix, Composite. Purpose: Self-contained learning units.',
    category: 'concept',
    seeAlso: ['Knowledge Object'],
    alternateDefinitions: [
      {
        definition: 'A UI component for displaying items in the application interface.',
        context: 'Technical UI component'
      }
    ]
  },
  {
    term: 'Tool',
    definition: 'A practical device for facilitating action or learning in the knowledge system. Example: Worksheets, Checklists, Assessments. Structure: Checklist, Template, Form. Purpose: Enable specific actions or measurements.',
    category: 'concept',
    seeAlso: ['Knowledge Object', 'Prioritization Tool'],
    alternateDefinitions: [
      {
        definition: 'A prioritization method/framework (e.g., RICE, ICE, WSJF).',
        context: 'Prioritization method'
      }
    ]
  },
  {
    term: 'Value',
    definition: 'A core value or principle in the knowledge system. Example: Clarity, Courage, Commitment. Structure: Conceptual Node, Value. Purpose: Guide decision-making and behavior.',
    category: 'concept',
    seeAlso: ['Knowledge Object'],
    alternateDefinitions: [
      {
        definition: 'The specific expression or selection assigned to an attribute for a given item. Can be quantitative (e.g., 5 hours, $10K, 3 FTEs) or qualitative (e.g., high, medium, low).',
        context: 'Attribute value in prioritization system'
      }
    ]
  },
  {
    term: 'Model',
    definition: 'A conceptual representation of specific realities in the knowledge system. Example: Dreyfus Model, S-Curve. Structure: Developmental Stages, Curve, Matrix. Purpose: Explain specific phenomena or processes.',
    category: 'concept',
    seeAlso: ['Knowledge Object'],
    alternateDefinitions: [
      {
        definition: 'A data model in the technical system representing entities and their relationships.',
        context: 'Technical data model'
      }
    ]
  },
  {
    term: 'Pattern',
    definition: 'A knowledge object type that represents a pattern or template.',
    category: 'concept',
    seeAlso: ['Knowledge Object'],
    alternateDefinitions: [
      {
        definition: 'A detected pattern in assessment responses that indicates a specific prioritization profile or challenge. The system evaluates 33 patterns.',
        context: 'Assessment pattern'
      }
    ]
  },

  // ============================================
  // Content Curation Workflow
  // ============================================
  {
    term: 'Content Curation Workflow',
    definition: 'Complete system from raw item intake to real-world execution. Also known as the 5 C\'s: Capture (Collect raw inputs with first-touch metadata), Categorize (Apply Meta-Taxonomy classification), Connect (Establish relationships and system membership), Compose (Create deliverables through Assembly Service), Curate (Manage authority, freshness, and lifecycle).',
    category: 'framework',
    seeAlso: ['Capture', 'Categorize', 'Connect', 'Compose', 'Curate']
  },
  {
    term: 'Capture',
    definition: 'Collect raw inputs with first-touch metadata. First step of the Content Curation Workflow (5 C\'s).',
    category: 'concept',
    seeAlso: ['Content Curation Workflow', 'Categorize', 'Connect', 'Compose', 'Curate']
  },
  {
    term: 'Categorize',
    definition: 'Apply Meta-Taxonomy classification. Second step of the Content Curation Workflow (5 C\'s).',
    category: 'concept',
    seeAlso: ['Content Curation Workflow', 'Capture', 'Connect', 'Compose', 'Curate', 'Meta-Taxonomy Framework']
  },
  {
    term: 'Connect',
    definition: 'Establish relationships and system membership. Third step of the Content Curation Workflow (5 C\'s).',
    category: 'concept',
    seeAlso: ['Content Curation Workflow', 'Capture', 'Categorize', 'Compose', 'Curate', 'Relationship Type']
  },
  {
    term: 'Compose',
    definition: 'Create deliverables through Assembly Service. Fourth step of the Content Curation Workflow (5 C\'s).',
    category: 'concept',
    seeAlso: ['Content Curation Workflow', 'Capture', 'Categorize', 'Connect', 'Curate']
  },
  {
    term: 'Curate',
    definition: 'Manage authority, freshness, and lifecycle. Fifth step of the Content Curation Workflow (5 C\'s).',
    category: 'concept',
    seeAlso: ['Content Curation Workflow', 'Capture', 'Categorize', 'Connect', 'Compose']
  },

  // ============================================
  // Meta-Frameworks & Modeling
  // ============================================
  {
    term: 'Meta-Taxonomy Framework',
    definition: 'The foundational classification system that enables precise categorization and rich interconnection of all knowledge objects. Provides the "grammar" for organizing, relating, and retrieving intellectual capital across multiple domains and use cases. Consists of five essential meta-categories: Category, Structure, Relationship, Instance, System.',
    category: 'framework',
    seeAlso: ['Representational Grammar', 'Knowledge Object', 'Object Type', 'Structure', 'Relationship Type']
  },
  {
    term: 'Representational Grammar',
    definition: 'A four-layer meta-model for describing, designing, and evaluating knowledge objects. Provides a unified grammar—nouns, adjectives, verbs, and purpose—that standardizes how we capture, relate, and reason about frameworks, models, tools, and artifacts. The 4 Layers: Ontology (What exists - nouns), Modality (How it is now - adjectives), Dynamics (How it changes - verbs/grammar), Evaluation (Why it matters - purpose & feedback).',
    category: 'framework',
    seeAlso: ['Meta-Taxonomy Framework', 'Ontology', 'Modality', 'Dynamics', 'Evaluation', '4-Layer Reality Model']
  },
  {
    term: 'Ontology',
    definition: 'What exists (nouns). First layer of Representational Grammar. Implements Meta-Taxonomy Framework.',
    category: 'concept',
    seeAlso: ['Representational Grammar', 'Modality', 'Dynamics', 'Evaluation']
  },
  {
    term: 'Modality',
    definition: 'How it is now (adjectives). Second layer of Representational Grammar. Implements State Classification Model.',
    category: 'concept',
    seeAlso: ['Representational Grammar', 'Ontology', 'Dynamics', 'Evaluation', 'State Classification Model']
  },
  {
    term: 'Dynamics',
    definition: 'How it changes (verbs/grammar). Third layer of Representational Grammar. Includes rules, processes, causality, uncertainty.',
    category: 'concept',
    seeAlso: ['Representational Grammar', 'Ontology', 'Modality', 'Evaluation']
  },
  {
    term: 'Evaluation',
    definition: 'Why it matters (purpose & feedback). Fourth layer of Representational Grammar. Includes intent, utility, observation, adaptation.',
    category: 'concept',
    seeAlso: ['Representational Grammar', 'Ontology', 'Modality', 'Dynamics']
  },
  {
    term: 'State Classification Model',
    definition: 'Describes the geometry of variation for any attribute or property. Lives in Layer 2 (Modality) as State Topology primitive. The 4 State Types: Toggle (Binary on/off), Dial (Continuum adjustable within limits), Multi-State (Discrete modes or categories), Nested System (Coupled dimensions that interact).',
    category: 'framework',
    seeAlso: ['Representational Grammar', 'Modality', 'Attribute']
  },
  {
    term: '4-Layer Reality Model',
    definition: 'Minimum set of meta-level primitives needed to capture and represent reality: Ontology (What exists - nouns), Modality (How properties can vary - adjectives), Dynamics (How things change and are steered - verbs), Evaluation (Why it matters and how we learn - aims/feedback).',
    category: 'framework',
    seeAlso: ['Representational Grammar', 'Ontology', 'Modality', 'Dynamics', 'Evaluation']
  },
  {
    term: '6 Primitives of System Modeling',
    definition: 'Universal language for documenting any product, app, or system: Actors (Who is involved), Components (What it\'s made of), Processes (What happens), Data (What it stores and relates), Interactions (What moves or triggers), Context of Purpose (Why it matters).',
    category: 'framework',
    seeAlso: ['Actors', 'Components', 'Processes', 'Data', 'Interactions', 'Context of Purpose']
  },
  {
    term: 'Actors',
    definition: 'Who is involved (users, systems, roles). One of the 6 Primitives of System Modeling.',
    category: 'concept',
    seeAlso: ['6 Primitives of System Modeling', 'Components', 'Processes', 'Data', 'Interactions', 'Context of Purpose']
  },
  {
    term: 'Components',
    definition: 'What it\'s made of (UI, APIs, services, databases). One of the 6 Primitives of System Modeling.',
    category: 'concept',
    seeAlso: ['6 Primitives of System Modeling', 'Actors', 'Processes', 'Data', 'Interactions', 'Context of Purpose']
  },
  {
    term: 'Processes',
    definition: 'What happens (workflows, transitions, logic). One of the 6 Primitives of System Modeling.',
    category: 'concept',
    seeAlso: ['6 Primitives of System Modeling', 'Actors', 'Components', 'Data', 'Interactions', 'Context of Purpose'],
    alternateDefinitions: [
      {
        definition: 'A structured workflow for prioritization. May follow DEGAP phases or other process models.',
        context: 'Technical workflow'
      }
    ]
  },
  {
    term: 'Data',
    definition: 'What it stores and relates (entities, attributes, relationships). One of the 6 Primitives of System Modeling.',
    category: 'concept',
    seeAlso: ['6 Primitives of System Modeling', 'Actors', 'Components', 'Processes', 'Interactions', 'Context of Purpose']
  },
  {
    term: 'Interactions',
    definition: 'What moves or triggers (actions, events, alerts). One of the 6 Primitives of System Modeling.',
    category: 'concept',
    seeAlso: ['6 Primitives of System Modeling', 'Actors', 'Components', 'Processes', 'Data', 'Context of Purpose']
  },
  {
    term: 'Context of Purpose',
    definition: 'Why it matters (scenario, outcome, job-to-be-done). One of the 6 Primitives of System Modeling.',
    category: 'concept',
    seeAlso: ['6 Primitives of System Modeling', 'Actors', 'Components', 'Processes', 'Data', 'Interactions']
  },
  {
    term: 'Interrogative Dimensions Framework',
    definition: 'Meta-model where each interrogative maps to core clarity and alignment domains: Who → Identity & Roles, What → Objects, Tasks, Outcomes, Where → Context & Environment, Why → Purpose & Motivation, How → Methods & Processes, When → Time & Timing, To What Extent → Scale & Proportionality.',
    category: 'framework',
    seeAlso: ['Desired Outcome Model']
  },
  {
    term: 'Desired Outcome Model',
    definition: 'A model consisting of six pointed questions designed to elicit clarifying responses, ensuring a crystal-clear understanding of what you are working toward.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'framework',
    seeAlso: ['Interrogative Dimensions Framework', 'DEGAP Process Model', 'Gather'],
    alternateDefinitions: [
      {
        definition: 'Six-question framework for clarifying objectives and ensuring alignment before prioritization begins: 1. What do you want? 2. What\'s even more important (meta outcome)? 3. What is the evidence for success? 4. What are the obstacles to success? 5. What are the potential negative unintended consequences? 6. What are the steps to success?',
        context: 'Peak Priorities System definition'
      }
    ]
  },
  {
    term: '6 Types of Potential Priorities',
    definition: 'Comprehensive categorization of what can be prioritized: People (Categories of people, groups, or constituencies), Places (Locations, both real and virtual), Things (Material, virtual, or imaginary objects), Activities (Projects, initiatives, actions, and tasks), Information (Data at any level of abstraction), Time (Processes, phases, workflows, sequencing, and transitions).',
    category: 'framework',
    seeAlso: ['Gather', 'DEGAP Process Model']
  },
  {
    term: '4-Layer Meta-Stack',
    definition: 'Complete architecture from conceptual frameworks to implementation: Layer 1 – Ontology (What Exists), Layer 2 – Modality (How Properties Can Vary), Layer 3 – Dynamics (How Things Change), Layer 4 – Evaluation (Why & Learning).',
    category: 'framework',
    seeAlso: ['Representational Grammar', '4-Layer Reality Model', 'Meta-Taxonomy Framework']
  },
  {
    term: 'Four-Layer Documentation Stack',
    definition: 'Move from big-picture abstraction to detailed build specifications: Vision Map (Conceptual) – Essential domains and relationships, Architecture Map (Structural) – Principal components and interfaces, Process Flow (Mechanistic) – How things actually happen across modules, Implementation Blueprint (Technical) – Real artifacts you can build/configure.',
    category: 'framework',
    seeAlso: ['4-Layer Meta-Stack', 'Representational Grammar']
  },

  // ============================================
  // Relevance Framework (existing entries)
  // ============================================
  {
    term: 'Relevance Framework',
    definition: 'A communication and decision-making model built on three sequential questions: What is it? Why is it important? How does it work? Used to ensure clarity, significance, and practical grounding in leadership, prioritization, and coaching contexts.',
    relatedFramework: 'gf.relevance.framework',
    category: 'framework',
    examples: [
      'Before introducing a new process, answer: What is this process? Why does it matter to our goals? How will we implement it?',
      'When coaching: What capability are we developing? Why is it critical for this role? How will we practice and measure it?'
    ],
    seeAlso: ['Coherence', 'Shallow Communication', 'Ungrounded Inspiration', 'Mechanical Instruction']
  },
  {
    term: 'Coherence',
    definition: 'Alignment between concept (what), purpose (why), and practice (how). Achieved when all three dimensions of the Relevance Framework are present and mutually reinforcing.',
    relatedFramework: 'gf.relevance.framework',
    category: 'concept',
    examples: [
      'A strategy has coherence when the vision (what), rationale (why), and execution plan (how) are clearly aligned',
      'A team demonstrates coherence when everyone can articulate what they\'re doing, why it matters, and how they\'re doing it'
    ],
    seeAlso: ['Relevance Framework', 'Shallow Communication', 'Ungrounded Inspiration', 'Mechanical Instruction']
  },
  {
    term: 'Shallow Communication',
    definition: 'Communication that provides definition without significance or application. An anti-pattern where only "what" is addressed, leaving stakeholders unclear on importance or implementation.',
    relatedFramework: 'gf.relevance.framework',
    category: 'anti-pattern',
    examples: [
      'Defining OKRs in a training without explaining their strategic value or how to write effective ones',
      'Introducing new terminology without connecting it to existing challenges or practical use'
    ],
    seeAlso: ['Ungrounded Inspiration', 'Mechanical Instruction', 'Relevance Framework', 'Coherence']
  },
  {
    term: 'Ungrounded Inspiration',
    definition: 'Inspiration that lacks clear definition or practical mechanics. An anti-pattern where only "why" is addressed, creating motivation without clarity or actionability.',
    relatedFramework: 'gf.relevance.framework',
    category: 'anti-pattern',
    examples: [
      'Rallying teams around "customer obsession" without defining what it means or how to practice it',
      'Vision casting without translating the vision into concrete goals or actions'
    ],
    seeAlso: ['Shallow Communication', 'Mechanical Instruction', 'Relevance Framework', 'Coherence']
  },
  {
    term: 'Mechanical Instruction',
    definition: 'Instructions on "how" without understanding of "what" or "why". An anti-pattern where only procedures are provided, leading to compliance without comprehension or adaptability.',
    relatedFramework: 'gf.relevance.framework',
    category: 'anti-pattern',
    examples: [
      'Following agile ceremonies by rote without understanding agile principles or their purpose',
      'Executing a prioritization process as checklist items without grasping the underlying logic'
    ],
    seeAlso: ['Shallow Communication', 'Ungrounded Inspiration', 'Relevance Framework', 'Coherence']
  },

  // ============================================
  // Technical Terms
  // ============================================
  {
    term: 'Item Status',
    definition: 'The status of a prioritization item. Valid statuses: ill-formed, defined, evaluated, prioritized, force-ranked.',
    category: 'concept',
    seeAlso: ['Item', 'Priority']
  },
  {
    term: 'Transition Validation',
    definition: 'Validation result when transitioning an item between lifecycle stages. Contains valid boolean, errors array, warnings array, and optional requiredActions array.',
    category: 'concept',
    seeAlso: ['Lifecycle Transition Path', 'Item', 'Priority']
  },
  {
    term: 'Tool Application Result',
    definition: 'Result when applying a prioritization tool to Attribute Management. Contains attributesCreated array, existingAttributes array, and errors array.',
    category: 'concept',
    seeAlso: ['Prioritization Tool', 'Attribute']
  },
  {
    term: 'Session',
    definition: 'The canonical unit of work. A configurable execution container where prioritization work occurs. Sessions are not meetings. They are structured work units. A Loop may contain multiple Sessions. Sessions produce Session Results. Loops produce Proposals.',
    category: 'concept',
    seeAlso: ['Session Result', 'Decision Class', 'Session Intent', 'Loop', 'Run']
  },
  {
    term: 'Decision Class',
    definition: 'The semantic definition of what decision is being made within a Session. Determines the decision product produced, the invariants and gating requirements, the ownership resolution model, and the allowed method/tool families. The canonical set is: Participation, Pool Selection, Criteria, Scales, Values, and Prioritization.',
    category: 'concept',
    seeAlso: ['Session', 'Session Type', 'Session Intent']
  },
  {
    term: 'Session Type',
    definition: 'User-facing label for a Decision Class. "Session Type" and "Decision Class" refer to the same semantic concept. Decision Class is the canonical system term.',
    category: 'concept',
    seeAlso: ['Decision Class', 'Session', 'Session Intent']
  },
  {
    term: 'Session Intent',
    definition: 'The operational purpose and workflow framing of a Session. Maps 1:1 to a Decision Class. Determines UX framing and workflow structure. Does not change the semantic invariants of the Decision Class.',
    category: 'concept',
    seeAlso: ['Decision Class', 'Session Type', 'Session']
  },
  {
    term: 'Session Result',
    definition: 'Immutable output of a single session. Informational. A Run produces a Session Result.',
    category: 'concept',
    seeAlso: ['Session', 'Run', 'Aggregated Result']
  },
  {
    term: 'Run',
    definition: 'A single execution of a method or tool within a Session that produces a persisted Session Result. A Run operates over a locked Item Pool and locked Criteria Set, produces an immutable Session Result, and may occur multiple times within a Session if configuration allows.',
    category: 'concept',
    seeAlso: ['Session', 'Session Result', 'Item Pool', 'Criteria Set']
  },
  {
    term: 'Ball Holder',
    definition: 'The role currently responsible for advancing the Ball at a given Tier within a Loop. Accountable for configuring appropriate Tracks, ensuring prerequisite conditions are satisfied, initiating or responding to governance transitions, and packaging and transmitting the Ball in its next form (Request or Proposal).',
    category: 'concept',
    seeAlso: ['Loop', 'Proposal', 'Request', 'Track']
  },
  {
    term: 'Published Priority List',
    definition: 'The authoritative prioritization outcome for a Track within a Cycle. Occurs only after Ratified → Published transition. An Item is a Priority when it occupies a ranked position in a Published Priority List.',
    category: 'concept',
    seeAlso: ['Item Pool', 'Priority', 'Governance', 'Track']
  },
  {
    term: 'Track',
    definition: 'The persistent work container anchored to a Comparison Cell = (Item Category × Altitude). Tracks exist because unlike Items cannot be meaningfully force-ranked without collapsing them into a coherent comparison space. Sessions do not cross Track boundaries. Aggregation does not cross Track boundaries. Each Track culminates in exactly one final ranking Session Result.',
    category: 'concept',
    seeAlso: ['Comparison Cell', 'Item Category', 'Altitude', 'Session']
  },
  {
    term: 'Comparison Cell',
    definition: 'The unit of comparability: (Item Category, Altitude) — one cell of the Comparison Grid; known in code and schema as track_cell. Tracks are anchored to Comparison Cells; sessions do not cross Track boundaries.',
    category: 'concept',
    seeAlso: ['Track', 'Item Category', 'Altitude', 'Assignment']
  },
  {
    term: 'Handoff',
    definition: 'A directional transfer event in Catchball where the "ball" (proposal, intent, or synthesis) moves from one organizational level to another. Handoffs can move upward (middle→executive), downward (executive→middle), or laterally. Each handoff is recorded as a governance event and may trigger a Loop Iteration boundary.',
    category: 'concept',
    examples: [
      'The middle management team completed their synthesis and initiated a handoff to executive for review.',
      'After the executive review, a handoff returned the proposal to the middle tier with revision requests.'
    ],
    seeAlso: ['Catchball', 'Loop Iteration', 'Ball Holder', 'Governance Event']
  },
  {
    term: 'Outcome',
    definition: 'An immutable Prioritization Session Result representing the final prioritized state within a Track. Once an Outcome is produced, it becomes the authoritative priority list for that Comparison Cell. Outcomes are append-only; they cannot be edited, only superseded by a new Cycle.',
    category: 'concept',
    examples: [
      'The cycle produced three Outcomes—one for each Comparison Cell in scope.',
      'We cannot change last quarter\'s Outcome; we need to run a new Cycle to re-prioritize.'
    ],
    seeAlso: ['Session Result', 'Track', 'Comparison Cell', 'Prioritize', 'Priority']
  },
  {
    term: 'Snapshot',
    definition: 'A saved state of a Item Pool at a point in time. Used for history tracking and comparison.',
    category: 'concept',
    seeAlso: ['Item Pool']
  },
  {
    term: 'Workspace',
    definition: 'A container for organizing work. Contains Items, Item Pools, and other resources. May be organized by team, project, or other dimensions.',
    category: 'concept',
    seeAlso: ['Item Pool', 'Item']
  },
  {
    term: 'View',
    definition: 'A specific way of viewing or organizing data in the system. Examples: Item Pool view, Focus Map view, Analytics view.',
    category: 'concept',
    seeAlso: ['Item Pool', 'Focus Map', 'Analytics']
  },
  {
    term: 'Canon Engine',
    definition: 'A system that maintains the authoritative source of truth for all Peak Priorities content, ensuring documentation integrity with terms and relationships, keeping documentation current with the system state, and managing versions of system parts.',
    category: 'framework',
    examples: [
      'Synthesizing canonical documentation from distributed markdown sources',
      'Validating new content against glossary definitions before promotion',
      'Generating changelogs to review changes before approving production updates'
    ],
    seeAlso: ['Documentation', 'Content Curation Workflow', 'Meta-Taxonomy Framework']
  },

  // ============================================
  // Managing Priorities Book Terms
  // ============================================
  {
    term: '80/20 rule',
    definition: 'A simple, universally applicable sorting method that suggests roughly 20% of the causes typically create approximately 80% of the effects. It helps you focus on the most significant items in a set.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Pareto Analysis']
  },
  {
    term: 'Absolute Scale',
    definition: 'A scale that requires assigning or calculating a definitive number to items, such as monetary value, often appropriate when quantifiable answers are needed and calculations must be defensible.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Scoring', 'Simple Weighted Scoring']
  },
  {
    term: 'Activities (as kind of item)',
    definition: 'A top-level category of items encompassing projects, initiatives, actions, and tasks.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['6 Types of Potential Priorities', 'Items']
  },
  {
    term: 'Air Sandwich',
    definition: 'The empty void in an organization between the high-level strategy (conjured up in the stratosphere) and the realization of that vision down on the ground.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'anti-pattern',
    seeAlso: ['North Star', 'Strategy']
  },
  {
    term: 'Analytic Hierarchy Process',
    definition: 'A hybrid prioritization method appropriate for complex efforts in larger organizations that combines Paired Comparison and sophisticated mathematical modeling. It provides a structured approach for decomposing abstract business drivers (strategy) into specific criteria, establishing a hierarchy of interconnected priorities.',
    source: 'Managing Priorities',
    qualityLevel: 2,
    category: 'framework',
    seeAlso: ['Paired Comparison', 'Prioritization Tool']
  },
  {
    term: 'Assumptions, Prioritizing',
    definition: 'Uncritically held beliefs about reality that silently shape how decisions are made, which must be identified and pressure tested to ensure critical decisions remain sound.',
    source: 'Managing Priorities',
    qualityLevel: 2,
    category: 'concept',
    seeAlso: ['Decide', 'Framing']
  },
  {
    term: 'Avoidances',
    definition: 'Items an individual is willfully ignoring, procrastinating on, or avoiding, which, when acknowledged and tackled, can release pent-up energy and significantly jumpstart motivation and productivity.',
    source: 'Managing Priorities',
    qualityLevel: 2,
    category: 'concept',
    seeAlso: ['Avoidance & Activation', 'Morning Boot Routine']
  },
  {
    term: 'Backlogs',
    definition: 'A list of items to be evaluated for future consideration, which serves as a container for any items that do not make the final list of active priorities.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Below the Line', 'Item Pool']
  },
  {
    term: 'Catchball',
    definition: 'A structured, bidirectional alignment pattern across organizational tiers within a Cycle. Catchball consists of one or more Loops. Executives (or originating authority) initiate intent downward as a Request. Downstream tiers refine and respond upward as a Proposal. Catchball is not a meeting or workflow step. It is an emergent property of correctly configured Cycles, Tracks, Loops, Sessions, and governance transitions.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Loop', 'Cycle', 'Proposal', 'Request', 'Ball Holder']
  },
  {
    term: 'Cycle',
    definition: 'The highest-level container of intent for prioritization. A Cycle begins when decision authority initiates a prioritization effort for a defined Scope. A Cycle may contain one or more Tracks, multiple Catchball Loops within each Track, and multiple Sessions within each Loop. A Cycle completes when all Tracks are either Accepted (ratified and published) or Finally Rejected.',
    category: 'concept',
    seeAlso: ['Track', 'Loop', 'Session', 'Catchball']
  },
  {
    term: 'Loop',
    definition: 'One complete downward and upward movement of the Ball within a Cycle. A Loop begins when a Request is issued from an originating Tier and sent downward. A Loop completes when a corresponding Proposal is returned upward. A Loop may contain multiple Sessions across one or more Tracks. A Loop does not imply acceptance. It represents one full iteration of refinement and response.',
    category: 'concept',
    seeAlso: ['Cycle', 'Request', 'Proposal', 'Session', 'Ball Holder']
  },
  {
    term: 'Request',
    definition: 'The downward form of the Ball. Includes intent and desired decision product, constraints and guardrails, scope context, required outputs, and time bounds.',
    category: 'concept',
    seeAlso: ['Proposal', 'Ball Holder', 'Loop', 'Catchball']
  },
  {
    term: 'Proposal',
    definition: 'The upward form of the Ball. Includes one or more Track final ranking Session Results, aggregation and rationale, tradeoffs, and evidence references.',
    category: 'concept',
    seeAlso: ['Request', 'Ball Holder', 'Loop', 'Session Result']
  },
  {
    term: 'Revision Request',
    definition: 'A governance action issued in response to a Proposal that requires further clarification, refinement, or adjustment. Initiates another Loop within the same Track. Does not terminate the Track\'s obligation within the Cycle. Does not erase historical artifacts. Preserves continuity of governance and accountability. Distinct from Final Rejection, which explicitly ends the Track\'s participation.',
    category: 'concept',
    seeAlso: ['Final Rejection', 'Proposal', 'Loop', 'Governance']
  },
  {
    term: 'Final Rejection',
    definition: 'An explicit termination of a Track\'s obligation within a Cycle without further revision. Final Rejection completes that Track\'s participation in the Cycle, does not erase historical artifacts, and does not prevent future Cycles from reconsidering the Items. Distinct from Revision Request, which initiates another Loop.',
    category: 'concept',
    seeAlso: ['Revision Request', 'Track', 'Cycle', 'Governance']
  },
  {
    term: 'Certified Information Privacy Professional',
    definition: 'A credential demonstrating that an individual understands privacy laws, regulations, and how to apply them, which is often required for privacy professional roles.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: []
  },
  {
    term: 'Commitments',
    definition: 'Agreements or contracts made, which limit options and outcomes; they come in two forms: hard (contractual) and soft (generally nonbinding, but affecting reputation/say-do ratio).',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Constraints', 'Dependencies']
  },
  {
    term: 'Constraints',
    definition: 'Critical limiting factors, such as minimum salary, location, or hours, that job seekers examine when evaluating a prospective opportunity.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Commitments', 'Criteria', 'Framing']
  },
  {
    term: 'Context',
    definition: 'The venue, problem space, or circumstance responsible for the gap between the current and desired state, which sets the boundary conditions and informs what is likely to be most and least important.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Framing', 'Comparison Set', 'Item Pool']
  },
  {
    term: 'Dependencies',
    definition: 'Items upon which a follow-up item relies; the more people who depend on a decision or output, the greater the priority and consequences of delaying that item.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Cost of Delay', 'Prioritization', 'Items']
  },
  {
    term: 'Episodic Prioritization',
    definition: 'Prioritization that is relegated to infrequent or one-off situations, such as making a complex decision or a large purchase.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Periodic Prioritization', 'Prioritization']
  },
  {
    term: 'Fast vs. Right Framework',
    definition: 'A framework that aids decision-making by helping individuals think through the trade-off between speed (how quickly one needs to act) and rigor (how correct the decision must be).',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'framework',
    seeAlso: ['Trade-off', 'Prioritization Tool']
  },
  {
    term: 'Frameworks',
    definition: 'Visual tools that illustrate concepts difficult to envision with words alone, creating a mental canvas for comparing items and helping establish a shared understanding by highlighting important dimensions.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Prioritization Tool', 'Eisenhower Matrix', 'Impact-Effort Matrix']
  },
  {
    term: 'Information (as kind of item)',
    definition: 'A top-level category of items that includes any type of data at any level of abstraction, such as goals, ideas, values, symptoms, and risk factors.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['6 Types of Potential Priorities', 'Items', 'Activities (as kind of item)', 'People (as kind of item)', 'Places (as kind of item)', 'Things (as kind of item)', 'Time (as kind of item)']
  },
  {
    term: 'Metadata',
    definition: 'Tidbits of information collected about each item, such as the source, owner, scope, or date collected, which helps ensure the information remains distinguishable and easily understandable.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Item', 'Attribute', 'Item Category']
  },
  {
    term: 'Morning Boot Routine',
    definition: 'An easy, effective daily routine, thought of as "DEGAP Lite," used to quickly apply core prioritization principles to clarify what counts, identify major avoidances, time-sensitive items, and the next strategic steps toward long-term goals.',
    source: 'Managing Priorities',
    qualityLevel: 2,
    category: 'technique',
    seeAlso: ['DEGAP Process Model', 'Avoidances', 'Prioritization']
  },
  {
    term: 'North Star',
    definition: 'The single priority, goal, metric, or mission that every other subsidiary priority should point toward, providing the basis for directional alignment within an organization.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Item Pool', 'Focus Map', 'Catchball', 'Strategy']
  },
  {
    term: 'Outcome Drivers',
    definition: 'A concise statement defining the core motivation, problem, or opportunity that compels a change, answering the question, "Why bother doing anything different?" by linking undesirable circumstances to actionable change.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Desired Outcome Model', 'Context', 'Framing']
  },
  {
    term: 'Paired Comparison',
    definition: 'A sorting technique that simplifies difficult prioritization efforts by decomposing the process into a series of simple, iterative binary choices, where items are compared head-to-head to determine relative importance.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Pairwise Ranking', 'Prioritization Tool', 'Stack Ranking']
  },
  {
    term: 'Periodic Prioritization',
    definition: 'Prioritization appropriate when items need to be updated or refreshed at regular, set intervals, such as daily, weekly, or quarterly.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Episodic Prioritization', 'Prioritization', 'Focus Horizon']
  },
  {
    term: 'Plan of Record',
    definition: 'A formal, living document or presentation used after prioritization that declares the organization\'s formalized priorities, strategy, vision, and timeline, which is then cascaded throughout the organization.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Item Pool', 'Prioritization', 'North Star']
  },
  {
    term: 'People (as kind of item)',
    definition: 'A top-level category of items referring to groups, constituencies, or specific categories of people.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['6 Types of Potential Priorities', 'Items', 'Activities (as kind of item)', 'Information (as kind of item)', 'Places (as kind of item)', 'Things (as kind of item)', 'Time (as kind of item)']
  },
  {
    term: 'Places (as kind of item)',
    definition: 'A top-level category of items referring to locations, both real and virtual, such as sites or queues.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['6 Types of Potential Priorities', 'Items', 'Activities (as kind of item)', 'Information (as kind of item)', 'People (as kind of item)', 'Things (as kind of item)', 'Time (as kind of item)']
  },
  {
    term: 'Priorities Discovery Sprint',
    definition: 'A timeboxed, structured approach to due diligence/investigation designed to quickly gather intelligence, awareness, and information about items (potential priorities) in complex situations.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Gather', 'Prioritization', 'DEGAP Process Model']
  },
  {
    term: 'Prioritization',
    definition: 'The process of identifying items of any type and arranging them in order of importance, superiority in rank, or privilege. It is the process of deciding what to consider.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['DEGAP Process Model', 'Prioritizing items', 'Items']
  },
  {
    term: 'Process Model',
    definition: 'A formal system and workflow that provides structure and helps ensure critical steps are not skipped, serving as a metaphorical bridge for closing the gap between the current state and a desired future state.',
    source: 'Managing Priorities',
    qualityLevel: 2,
    category: 'framework',
    seeAlso: ['DEGAP Process Model', 'Prioritization']
  },
  {
    term: 'Prune the Product Tree',
    definition: 'A framework/innovation game that uses the metaphor of a tree to visualize and prioritize items (branches) which should be cut or pruned based on their value, impact, or feasibility, clarifying what is core versus peripheral.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Simulations', 'Prioritization Tool']
  },
  {
    term: 'RICE',
    definition: 'A pseudo-quantitative set of criteria (Reach, Impact, Confidence, and Effort) used in product design and development that ranks items using a form of Simple Weighted Scoring.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'framework',
    seeAlso: ['RICE Framework', 'Simple Weighted Scoring', 'Prioritization Tool']
  },
  {
    term: 'Simulations',
    definition: 'Interactive "games" that provide a safe venue for exploring conversations, making trade-offs, and testing decisions and actions that would otherwise carry real-world consequences.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'technique',
    seeAlso: ['Dot Voting', 'Buy a Feature', 'Prune the Product Tree']
  },
  {
    term: 'Stakeholder',
    definition: 'Any person who has an interest (a stake) in what priorities are chosen and the resulting outcomes, including colleagues, customers, suppliers, and, in personal prioritization, family and internal mental personas.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Stakeholder Intelligence', 'Engage', 'Gather']
  },
  {
    term: 'Things (as kind of item)',
    definition: 'A category of potential priorities representing inanimate objects such as equipment, machinery, hardware, products, services, or tools.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['6 Types of Potential Priorities', 'Items', 'Activities (as kind of item)', 'Information (as kind of item)', 'People (as kind of item)', 'Places (as kind of item)', 'Time (as kind of item)']
  },
  {
    term: 'Time (as kind of item)',
    definition: 'A top-level category of items that includes processes, phases, workflows, sequencing, system effects, and transitions.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['6 Types of Potential Priorities', 'Items', 'Activities (as kind of item)', 'Information (as kind of item)', 'People (as kind of item)', 'Places (as kind of item)', 'Things (as kind of item)']
  },
  {
    term: 'Trade-off',
    definition: 'An explicit choice made due to limited resources (time, attention, energy, or money), where prioritizing forms the mental software that empowers people to make tough decisions, often elevating one option while delaying another.',
    source: 'Managing Priorities',
    qualityLevel: 3,
    category: 'concept',
    seeAlso: ['Tradeoff Intelligence', 'Prioritization', 'Cut Line']
  },
  {
    term: 'Urgency',
    definition: 'The dimension in the Eisenhower Matrix defined as the sense of pressure that time, or the lack thereof, creates.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'concept',
    seeAlso: ['Eisenhower Matrix', 'Criteria', 'Cost of Delay']
  },
  {
    term: 'Weighted Smallest Job First',
    definition: 'A sorting technique often used in Agile environments that ranks items by dividing their Cost of Delay by the job duration, prioritizing work that delivers the most value in the shortest time.',
    source: 'Managing Priorities',
    qualityLevel: 1,
    category: 'framework',
    seeAlso: ['WSJF', 'Cost of Delay', 'Prioritization Tool']
  },
  // ============================================
  // Organizational accountability (added for /method)
  // ============================================
  {
    term: 'Say-Do Ratio',
    definition: 'The consistency between the priorities a team or leader states and the priorities they actually act on — a measure of whether stated priorities are real.',
    source: 'Managing Priorities',
    qualityLevel: 3,
    category: 'concept',
    seeAlso: ['Organizational Dissonance', 'Priority', 'Commitments']
  },
  {
    term: 'Necessary Endings',
    definition: 'The discipline of deliberately stopping low-value work, projects, or commitments so resources and attention are freed for higher priorities — treated in Managing Priorities as a companion practice to prioritization itself.',
    source: 'Managing Priorities',
    qualityLevel: 3,
    category: 'concept',
    seeAlso: ['Prioritize', 'Trade-off', 'Backlogs']
  },
  {
    term: 'Organizational Dissonance',
    definition: 'The gap between an organization\'s stated priorities and what its people actually spend time and resources on — a warning sign that prioritization is happening informally, or not at all.',
    source: 'Managing Priorities',
    qualityLevel: 3,
    category: 'concept',
    seeAlso: ['Say-Do Ratio', 'Hierarchy of Priorities', 'DEGAP Process Model']
  },
  {
    term: 'Hierarchy of Priorities',
    definition: 'A structured ranking of goals and initiatives across levels of an organization, so team- and individual-level priorities can be traced back to the priorities set above them.',
    source: 'Managing Priorities',
    qualityLevel: 3,
    category: 'concept',
    seeAlso: ['Max Priorities Pyramid', 'North Star', 'Organization']
  }
];

export const getGlossaryEntryByTerm = (term: string): GlossaryEntry | undefined => {
  return glossaryEntries.find(entry => 
    entry.term.toLowerCase() === term.toLowerCase()
  );
};

export const getGlossaryEntriesByCategory = (category: GlossaryEntry['category']): GlossaryEntry[] => {
  return glossaryEntries.filter(entry => entry.category === category);
};

export const getGlossaryEntriesByFramework = (encodedId: string): GlossaryEntry[] => {
  return glossaryEntries.filter(entry => entry.relatedFramework === encodedId);
};

export const searchGlossary = (searchTerm: string): GlossaryEntry[] => {
  const lowerSearch = searchTerm.toLowerCase();
  return glossaryEntries.filter(entry =>
    entry.term.toLowerCase().includes(lowerSearch) ||
    entry.definition.toLowerCase().includes(lowerSearch) ||
    entry.examples?.some(ex => ex.toLowerCase().includes(lowerSearch)) ||
    entry.seeAlso?.some(term => term.toLowerCase().includes(lowerSearch))
  );
};

// Conceptual/Family Groups - Terms organized by related concepts
export const conceptualGroups: ConceptualGroup[] = [
  {
    name: 'DEGAP: Five Stages of Transformation',
    description: 'The systematic five-stage journey from current state to desired future. Each stage builds on the previous, creating a coherent transformation process.',
    relatedFramework: 'pf.degap.stages',
    terms: ['DEGAP Process Model', 'Decide', 'Engage', 'Gather', 'Arrange', 'Prioritize']
  },
  {
    name: 'Relevance Framework Family',
    description: 'Terms related to the three-question framework for creating coherent communication and avoiding common anti-patterns.',
    relatedFramework: 'gf.relevance.framework',
    terms: ['Relevance Framework', 'Coherence', 'Shallow Communication', 'Ungrounded Inspiration', 'Mechanical Instruction']
  },
  {
    name: 'Lifecycle Stages: Item Evolution',
    description: 'The progression of items from initial idea to committed priority. Shows how items mature through the system.',
    terms: ['Item', 'Priority', 'Lifecycle State', 'Lifecycle Transition Path']
  },
  {
    name: 'Lifecycle Stages: Workflow Progression',
    description: 'The four workflow stages that track how items move through the prioritization system.',
    terms: ['Intake', 'Structuring', 'Ranking', 'Execution', 'Lifecycle Stage']
  },
  {
    name: 'Structuring & Attributes',
    description: 'Terms related to structuring items through attribute assignment and value setting.',
    terms: ['Attribute', 'Value', 'Criteria', 'Scoring', 'Framing', 'Slotting', 'Structuring Completeness', 'Meets Prerequisites', 'Item Manager']
  },
  {
    name: 'Item Pool Structure',
    description: 'Terms related to how priorities are organized, ranked, and managed in Item Pools.',
    terms: ['Item Pool', 'Cut Line', 'Above the Line', 'Below the Line', 'Focus Map', 'Comparison Set', 'Dimension']
  },
  {
    name: 'Focus Horizons',
    description: 'Time-based horizons that define the expected validity and review cadence for Item Pools.',
    terms: ['Focus Horizon', 'Short-term', 'Quarterly', 'Mid-term', 'Annual', 'Long-term', 'Strategic']
  },
  {
    name: 'Focus Drift & Realignment',
    description: 'Terms related to detecting and correcting misalignment between declared priorities and actual work.',
    terms: ['Focus Drift', 'Focus Realignment', 'Reprioritization Trigger', 'Stale Priority', 'Drift Event']
  },
  {
    name: 'Roles & Permissions',
    description: 'The six core roles and permission system that govern access and responsibilities in the prioritization system.',
    terms: ['List Owner', 'Item Manager', 'Criteria Authority', 'Focus Facilitator', 'Cross-Map Lead', 'Strategist', 'Permission Tier', 'Actor Role']
  },
  {
    name: 'System Architecture',
    description: 'Terms related to the platform architecture including System Modeling Spaces and Architecture Layers.',
    terms: ['System Modeling Space', 'Architecture Layer']
  },
  {
    name: 'Prioritization Tools & Methods',
    description: 'Frameworks and techniques for evaluating and ranking items.',
    terms: ['Prioritization Tool', 'RICE Framework', 'ICE Framework', 'WSJF', 'Impact-Effort Matrix', 'Eisenhower Matrix', 'MoSCoW Method', 'Stack Ranking', 'Pairwise Ranking', 'Dot Voting', 'Buy a Feature', 'Cost of Delay', 'Pareto Analysis', 'Simple Weighted Scoring', 'Max Priorities Pyramid']
  },
  {
    name: '12 Capabilities Model',
    description: 'Comprehensive model of prioritization competencies organized into four families.',
    relatedFramework: 'pf.12.capabilities',
    terms: ['12 Capabilities Model', 'Capability', 'Situational Awareness', 'Conceptual Precision', 'Cost of Delay Thinking', 'Rhythm & Ritual', 'Process Discipline', 'Tool Fluency', 'Stakeholder Intelligence', 'Cultural Reinforcement', 'Tradeoff Intelligence', 'Clarity of Focus', 'Avoidance & Activation', 'Outcome Reflection']
  },
  {
    name: 'Capability Families',
    description: 'The four coherent domains that organize the 12 capabilities.',
    relatedFramework: 'pf.12.capabilities',
    terms: ['Capability Family', 'Contextual Awareness', 'Execution Rhythms', 'Relational Navigation', 'Motivational Activation']
  },
  {
    name: 'Capability Orientations',
    description: 'Groupings of capabilities that share a common focus across families.',
    relatedFramework: 'pf.12.capabilities',
    terms: ['Orientation', 'Self-Oriented', 'System-Oriented', 'Sense & Align', 'Activate & Execute']
  },
  {
    name: 'Assessment & Scoring',
    description: 'Terms related to the prioritization assessment system and scoring mechanisms.',
    terms: ['Assessment Question', 'Question Part', 'Doing', 'Knowing', 'Gap', 'Response Option', 'Scope Level', 'Individual', 'Team', 'Organization', 'Capability Score', 'Family Score', 'Overall Score', 'Orientation Score', 'Pattern', 'Archetype', 'Progression Level']
  },
  {
    name: 'Knowledge Engine & Content',
    description: 'Terms related to the knowledge object system and content management.',
    terms: ['Knowledge Object', 'Object Type', 'Object Subtype', 'Relationship Type', 'Structure', 'System', 'Card', 'Tool', 'Value', 'Model', 'Pattern']
  },
  {
    name: 'Content Curation Workflow (5 C\'s)',
    description: 'The five-step workflow for managing content from capture to curation.',
    terms: ['Content Curation Workflow', 'Capture', 'Categorize', 'Connect', 'Compose', 'Curate']
  },
  {
    name: 'Meta-Frameworks & Modeling',
    description: 'High-level frameworks for organizing and representing knowledge and systems.',
    terms: ['Meta-Taxonomy Framework', 'Representational Grammar', 'Ontology', 'Modality', 'Dynamics', 'Evaluation', 'State Classification Model', '4-Layer Reality Model', '6 Primitives of System Modeling', 'Actors', 'Components', 'Processes', 'Data', 'Interactions', 'Context of Purpose', 'Interrogative Dimensions Framework', 'Desired Outcome Model', '6 Types of Potential Priorities', '4-Layer Meta-Stack', 'Four-Layer Documentation Stack']
  }
];
